import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { ArrowLeft, ArrowRight, Check, Clock, Banknote, User, Mail, Phone, FileText, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Navbar from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const preSelectedService = location.state?.serviceId || null;

  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", customer_address: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [content, setContent] = useState({});

  useEffect(() => {
    axios.get(`${API}/services`).then(r => {
      setServices(r.data);
      if (preSelectedService) {
        const found = r.data.find(s => s.id === preSelectedService);
        if (found) { setSelectedService(found); setStep(2); }
      }
    });
    axios.get(`${API}/schedule`).then(r => setSchedule(r.data));
    axios.get(`${API}/content`).then(r => setContent(r.data)).catch(() => {});
  }, [preSelectedService]);

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingTimes(true);
    setSelectedTime(null);
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    axios.get(`${API}/bookings/available?date=${dateStr}`)
      .then(r => setAvailableTimes(r.data.available_times))
      .catch(() => setAvailableTimes([]))
      .finally(() => setLoadingTimes(false));
  }, [selectedDate]);

  const isDateDisabled = (date) => {
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    const jsDay = date.getDay();
    const pyDay = jsDay === 0 ? 6 : jsDay - 1;
    const daySchedule = schedule.find(s => s.day_of_week === pyDay);
    return !daySchedule || !daySchedule.active || daySchedule.times.length === 0;
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.customer_address) {
      toast.error(content.booking_required_field_error || "Vennligst fyll ut alle obligatoriske felt");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        service_id: selectedService.id,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        customer_address: formData.customer_address,
        notes: formData.notes,
      });
      setBooking(res.data);
      setStep(4);
      toast.success("Booking bekreftet!");
    } catch (err) {
      toast.error(err.response?.data?.detail || (content.booking_submit_error || "Noe gikk galt. Prøv igjen."));
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { num: 1, label: content.booking_progress_service || "Behandling" },
    { num: 2, label: content.booking_progress_datetime || "Dato & tid" },
    { num: 3, label: content.booking_progress_details || "Dine detaljer" },
    { num: 4, label: content.booking_progress_confirmation || "Bekreftelse" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 md:pt-28 pb-20 max-w-4xl mx-auto px-6 md:px-12">
        {/* Progress */}
        <div data-testid="booking-progress" className="flex items-center justify-center gap-2 mb-12">
          {steps.map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                step >= s.num ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}>
                {step > s.num ? <Check size={14} /> : s.num}
              </div>
              <span className={`hidden sm:inline ml-2 text-sm ${step >= s.num ? "text-foreground" : "text-muted-foreground"}`}>
                {s.label}
              </span>
              {i < steps.length - 1 && <div className={`w-8 md:w-16 h-px mx-2 ${step > s.num ? "bg-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Choose Service */}
        {step === 1 && (
          <div data-testid="step-1-services" className="animate-fade-up">
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-3">{content.booking_step1_title || "Velg behandling"}</h1>
            <p className="text-muted-foreground mb-10">{content.booking_step1_description || "Velg den massasjen som passer best for deg."}</p>
            <div className="grid gap-6">
              {services.map((service, i) => (
                <div
                  key={service.id}
                  data-testid={`booking-service-${i}`}
                  onClick={() => { setSelectedService(service); setStep(2); }}
                  className={`flex gap-6 bg-card rounded-2xl p-4 cursor-pointer transition-all hover:translate-y-[-2px] shadow-[0_4px_20px_rgb(0,0,0,0.04)] border-2 ${
                    selectedService?.id === service.id ? "border-primary" : "border-transparent"
                  }`}
                >
                  <img src={service.image_url} alt={service.name} className="w-28 h-28 md:w-36 md:h-36 rounded-xl object-cover flex-shrink-0" />
                  <div className="flex flex-col justify-center">
                    <h3 className="font-serif text-xl md:text-2xl mb-1">{service.name}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-3 line-clamp-2">{service.description}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Banknote size={14} /> {service.price} kr</span>
                      <span className="flex items-center gap-1"><Clock size={14} /> {service.duration} min</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Date & Time */}
        {step === 2 && (
          <div data-testid="step-2-datetime" className="animate-fade-up">
            <button data-testid="back-to-step-1" onClick={() => setStep(1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
              <ArrowLeft size={16} /> {content.booking_btn_back || "Tilbake"}
            </button>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-3">{content.booking_step2_title || "Velg dato og tid"}</h1>
            <p className="text-muted-foreground mb-10">
              {selectedService?.name} &mdash; {selectedService?.price} kr, {selectedService?.duration} min
            </p>
            <div className="grid md:grid-cols-2 gap-10">
              <div className="bg-card rounded-2xl p-6 shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
                <Calendar
                  data-testid="booking-calendar"
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  locale={nb}
                  disabled={isDateDisabled}
                  className="mx-auto"
                />
              </div>
              <div>
                <h3 className="font-serif text-xl mb-4">
                  {selectedDate ? format(selectedDate, "EEEE d. MMMM", { locale: nb }) : (content.booking_step2_description || "Velg en dato")}
                </h3>
                {!selectedDate && <p className="text-muted-foreground text-sm">{content.booking_no_date_selected || "Velg en dato i kalenderen for å se ledige tider."}</p>}
                {selectedDate && loadingTimes && <p className="text-muted-foreground text-sm">{content.booking_loading_times || "Laster ledige tider..."}</p>}
                {selectedDate && !loadingTimes && availableTimes.length === 0 && (
                  <p className="text-muted-foreground text-sm">{content.booking_no_times_available || "Ingen ledige tider denne dagen."}</p>
                )}
                {selectedDate && !loadingTimes && availableTimes.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    {availableTimes.map(time => (
                      <button
                        key={time}
                        data-testid={`time-slot-${time}`}
                        onClick={() => setSelectedTime(time)}
                        className={`rounded-xl py-3 text-sm font-medium transition-all hover:scale-105 ${
                          selectedTime === time
                            ? "bg-primary text-primary-foreground shadow-lg"
                            : "bg-muted text-foreground hover:bg-muted/80"
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                )}
                {selectedTime && (
                  <Button
                    data-testid="continue-to-step-3"
                    onClick={() => setStep(3)}
                    className="mt-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-base transition-all hover:scale-105 w-full"
                  >
                    {content.booking_btn_continue || "Gå videre"} <ArrowRight className="ml-2" size={18} />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Customer Details */}
        {step === 3 && (
          <div data-testid="step-3-details" className="animate-fade-up max-w-lg mx-auto">
            <button data-testid="back-to-step-2" onClick={() => setStep(2)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
              <ArrowLeft size={16} /> {content.booking_btn_back || "Tilbake"}
            </button>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-3">{content.booking_step3_title || "Dine detaljer"}</h1>
            <p className="text-muted-foreground mb-10">
              {selectedService?.name} &mdash; {selectedDate && format(selectedDate, "d. MMMM yyyy", { locale: nb })} kl. {selectedTime}
            </p>
            <div className="space-y-6">
              <div>
                <label className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><User size={14} /> {content.booking_label_name || "Fullt navn *"}</label>
                <Input
                  data-testid="input-name"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder={content.booking_placeholder_name || "Ola Nordmann"}
                  className="bg-transparent border-b-2 border-primary/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary h-12 text-base"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><Mail size={14} /> {content.booking_label_email || "E-post *"}</label>
                <Input
                  data-testid="input-email"
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  placeholder={content.booking_placeholder_email || "ola@eksempel.no"}
                  className="bg-transparent border-b-2 border-primary/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary h-12 text-base"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><Phone size={14} /> {content.booking_label_phone || "Telefon *"}</label>
                <Input
                  data-testid="input-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder={content.booking_placeholder_phone || "+47 900 00 000"}
                  className="bg-transparent border-b-2 border-primary/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary h-12 text-base"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><MapPin size={14} /> {content.booking_label_address || "Adresse *"}</label>
                <Input
                  data-testid="input-address"
                  type="text"
                  value={formData.customer_address}
                  onChange={e => setFormData({ ...formData, customer_address: e.target.value })}
                  placeholder={content.booking_placeholder_address || "Gateadresse, postnummer, by"}
                  className="bg-transparent border-b-2 border-primary/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary h-12 text-base"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><FileText size={14} /> {content.booking_label_notes || "Merknad (valgfritt)"}</label>
                <Textarea
                  data-testid="input-notes"
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder={content.booking_placeholder_notes || "Noe vi bør vite?"}
                  className="bg-transparent border-2 border-primary/20 rounded-xl focus-visible:ring-0 focus-visible:border-primary text-base min-h-[80px]"
                />
              </div>
              <Button
                data-testid="submit-booking-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-base transition-all hover:scale-105 w-full mt-4"
              >
                {submitting ? (content.booking_submitting || "Bestiller...") : (content.booking_btn_submit || "Bekreft booking")}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && booking && (
          <div data-testid="step-4-confirmation" className="animate-fade-up text-center max-w-lg mx-auto">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-8">
              <Check className="text-primary" size={36} />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-4">{content.booking_step4_title || "Takk for din bestilling!"}</h1>
            <p className="text-muted-foreground mb-10">{content.booking_step4_description || "Din booking er bekreftet. Vi gleder oss til å se deg."}</p>
            <div className="bg-card rounded-2xl p-8 text-left shadow-[0_4px_20px_rgb(0,0,0,0.04)] space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">Behandling</span>
                <span className="font-medium text-sm">{booking.service_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">Dato</span>
                <span className="font-medium text-sm">{selectedDate && format(selectedDate, "d. MMMM yyyy", { locale: nb })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">Tid</span>
                <span className="font-medium text-sm">{booking.time}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">Navn</span>
                <span className="font-medium text-sm">{booking.customer_name}</span>
              </div>
              {booking.customer_address && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Adresse</span>
                  <span className="font-medium text-sm">{booking.customer_address}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">Pris</span>
                <span className="font-medium text-sm">{selectedService?.price} kr</span>
              </div>
            </div>
            <Button
              data-testid="back-to-home-btn"
              onClick={() => navigate("/")}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-base transition-all hover:scale-105 mt-10"
            >
              {content.booking_btn_back_home || "Tilbake til forsiden"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
